import React, {useCallback, useMemo, useState, useEffect} from "react";
import classnames from 'classnames';
import {getHotspotHorizontalPadding, getHotspotStyle} from "utils/hotspot-helper";

import {ENGAGEMENT_SLIDE_DURATION} from "compounds/overlay-invideo";

import style from './style.module.scss';
import effectStyle from 'common/styles/effects.module.scss';
import {getHotspotMarkerConfig, getHotspotTextAlignStyles} from "../hotspotIcon";
import {
  HOTSPOT_ANIMATION_DURATION, 
  HotSpotType, 
  EFFECT_PREFIX, 
  EFFECT_TYPE, 
  ATTENTION_ANIMATION_DELAY, 
  ATTENTION_ANIMATION_STAGGER,
  ATTENTION_ANIMATION_DURATION,
  MARKER,
} from "utils/constants";

import {getMarkerRatio} from "utils/engagement-helper";

const hotspotAnimationDuration = `${HOTSPOT_ANIMATION_DURATION}ms`;

export function Hotspot(
  {
    hotspot: rawHotspot,
    onClick,
    containerDimensions,
    showing,
    isCardHotspot,
    useHotspotAnimation,
    noPointer,
    isSurveyOption,
    index,
    isSelected,
    isCreativeStudioPreviewMode,
    selectedHotspotSelector,
    hotspotPopperSelector,
    snappingHotspotSelector,
    showAttentionAnimation,
    animationIndex,
    onMouseOver = ()=>{},
    onMouseOut = ()=>{},
    resetKey,
    delayInitialAttentionAnimation,
    delayInitialAttentionAnimationForInVideo,
    onImageLoaded = () => {}
  }
) {
  const textAlignStyle = useMemo(() => getHotspotTextAlignStyles(rawHotspot), [rawHotspot]);
  const {
    content,
    hotspot
  } = useMemo(() => {
    return getHotspotMarkerConfig(
      rawHotspot,
      textAlignStyle,
      hotspotPopperSelector,
      isSelected,
      isCreativeStudioPreviewMode
    )
  }, [isCreativeStudioPreviewMode, hotspotPopperSelector, isSelected, rawHotspot, textAlignStyle]);

  const [touched, setTouched] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);


  const hotspotStyle = useMemo(() => getHotspotStyle(hotspot, containerDimensions.height), [containerDimensions.height, hotspot]);
  const {useAnimation, linkToUrl, hotspotType, hasChildren} = rawHotspot;

  const markerRatio = useMemo(() => getMarkerRatio(rawHotspot?.marker, rawHotspot?.customMarker), [rawHotspot]);

  const customHotspotClasses = useMemo(() => {
    if (selectedHotspotSelector && snappingHotspotSelector) {
      return classnames({
        [selectedHotspotSelector]: isSelected,
        [snappingHotspotSelector]: !isSelected
      })
    }
    return null
  }, [isSelected, selectedHotspotSelector, snappingHotspotSelector])

  const showPointer = useMemo(() => {
    if (isSurveyOption) {
      return true
    }

    if (noPointer) {
      return false;
    }

    if (hasChildren) {
      return true;
    }

    return Boolean(linkToUrl) || HotSpotType[hotspotType] === HotSpotType.ENGAGEMENT_EXPERIENCE;
  }, [isSurveyOption, hasChildren, linkToUrl, noPointer, hotspotType]);

  const hasEffect = useMemo(() => {
    return (hotspot.effect && hotspot.effect.effectType !== EFFECT_PREFIX.NONE) ? true : false
  }, [hotspot])

  const effectName = useMemo(() => {
    if (hasEffect) {
      if (EFFECT_TYPE.SHAKE === hotspot.effect.effectType && MARKER.DEFAULT !== hotspot.marker) {
        return `${hotspot.effect.effectType?.toLowerCase()}-content`;
      } else {
        return hotspot.effect.effectType?.toLowerCase()
      }
    } else {
      return EFFECT_TYPE.NONE.toLowerCase()
    }
  }, [hasEffect, hotspot])

  // Introduce a single frame delay when we remove attention animation and allowing hover effects 
  // so that the base class attributes can be reapplied, otherwise the initial hover "jumps"
  const [hoverEnabled, setHoverEnabled] = useState(false);
  useEffect(() => {
    if (hasEffect && !showAttentionAnimation) {
      const id = window.requestAnimationFrame(() => {
        setHoverEnabled(true);
      });

      return () => window.cancelAnimationFrame(id);
    }
    setHoverEnabled(false);
  }, [hasEffect, showAttentionAnimation]);

  // Introduce a single frame delay for touched so that we can remove attention on touch and then a frame later add 
  // the base effect style which produces an animation, otherwise there is a "jump" on touch
  const [touchDelayed, setTouchedDelayed] = useState(false);
  useEffect(() => {
    if (touched) {
      const id = window.requestAnimationFrame(() => {
        setTouchedDelayed(true);
      });

      return () => window.cancelAnimationFrame(id);
    }
    setTouchedDelayed(false)
  }, [touched]);
  
  const classes = useMemo(() => {
    return classnames(
      style.hotspot,
      showPointer ? null : style.noPointer,
      (useAnimation && useHotspotAnimation) ? style.useHotspotAnimation : null,
      isCardHotspot ? style.cardHotspot : style.videoHotspot,
      showing ? null : style.hidden,
      customHotspotClasses,
      hasEffect ? `${effectStyle[`${EFFECT_PREFIX.PERM}${effectName}`]} ${effectStyle.vgFxNoTouchHighlight}` : null,
      hoverEnabled && !touched ? `${effectStyle[`${EFFECT_PREFIX.HOVER}${effectName}`]}` : null,
      showAttentionAnimation && !touched ? `${effectStyle[`${EFFECT_PREFIX.ATTENTION}${effectName}`]}` : null,
      touchDelayed ? `${effectStyle[`${EFFECT_PREFIX.BASE}${effectName}`]}` : null,
    );
  }, [customHotspotClasses, isCardHotspot, showPointer, showing, useAnimation, useHotspotAnimation, hasEffect, hoverEnabled, touched, touchDelayed, effectName, showAttentionAnimation]);

  const horizontalPadding = useMemo(() => getHotspotHorizontalPadding(rawHotspot, containerDimensions.height), [containerDimensions.height, rawHotspot]);
  const padding = useMemo(() => `0px ${horizontalPadding}px`, [horizontalPadding]);

  const styles = useMemo(() => {
    return {
      ...hotspotStyle, 
      '--vg-fx-original-background-color': hotspotStyle.backgroundColor,
      '--vg-fx-original-border': hotspotStyle.border,
      '--vg-fx-original-border-color': hotspotStyle.borderColor,
      '--vg-fx-original-border-width': hotspotStyle.borderWidth,
      '--vg-fx-original-border-radius': hotspotStyle.borderRadius,
      '--vg-fx-original-border-style': hotspotStyle.borderStyle,  
      '--vg-fx-original-outline-color': hotspotStyle.outlineColor,
      '--vg-fx-original-outline-width': hotspotStyle.outlineWidth,
      '--vg-fx-original-outline-style': hotspotStyle.outlineStyle, 
      '--vg-fx-original-font-weight': hotspotStyle.fontWeight,  
      '--hotspotAnimationDuration': hotspotAnimationDuration, 
      ...textAlignStyle,
      padding,
      ...(showAttentionAnimation && {
        '--vg-fx-attention-duration': `${ATTENTION_ANIMATION_DURATION}s`,
        '--vg-fx-attention-delay': `${(animationIndex * ATTENTION_ANIMATION_STAGGER) + (delayInitialAttentionAnimation ? ATTENTION_ANIMATION_DELAY : 0) + (delayInitialAttentionAnimationForInVideo ? ENGAGEMENT_SLIDE_DURATION / 1000 : 0)}s`,
      }),
    }
  }, [hotspotStyle, padding, textAlignStyle, showAttentionAnimation, animationIndex, delayInitialAttentionAnimation, delayInitialAttentionAnimationForInVideo]);

  const contentStyles = useMemo(() => {
    // get size from %
    // slice off the % sign, divide by 100 to get as decimal, multiply by the player size.
    // subtract border width from container width and height.
    const borderWidth = parseFloat((styles.borderWidth || '0px').slice(0, -2));
    const width = ((styles.width.slice(0, -1) / 100) * containerDimensions.width) - (borderWidth * 2);
    const height = ((styles.height.slice(0, -1) / 100) * containerDimensions.height) - (borderWidth * 2);
    return {
      fontSize: styles.fontSize,
      width: width - (2 * horizontalPadding),
      height,
    };
  }, [containerDimensions.height, containerDimensions.width, horizontalPadding, styles.borderWidth, styles.fontSize, styles.height, styles.width]);

  
  const onHotspotClick = useCallback((e) => {
    onClick(hotspot, index, isSelected, e.shiftKey)
  }, [hotspot, index, isSelected, onClick]);

  const handleHotspotClick = (evt) => {
    if (touched) {
      // OV-405 prevent multiple submissions
      if (!isAnimating) {
        setIsAnimating(true)
        const event = {...evt} // copy event because it gets released before timeout function runs
        setTimeout(() => {
          handleTouchEnd(event)
          onHotspotClick(event)
          setIsAnimating(false)
        }, 600); // 0.6 second delay.
      }
    } else {
      onHotspotClick(evt);
    }
  }

  const handleTouchStart = (evt) => {
    if (evt.currentTarget && hasEffect) {
      setTouched(true)
    }
  }

  const handleTouchEnd = (evt) => {
    if (evt.currentTarget && hasEffect) {
      setTouched(false) // reset touched state
    }
  }

  /**
   * add hover class back on mouse over if it has been removed.
   * this supports devices with touch and mouse capabilities
   */
  const handleMouseOver = (evt) => {
    if (!touched && evt.currentTarget && hasEffect) {
      const style = effectStyle[`${EFFECT_PREFIX.HOVER}${effectName}`]
      // only add style if needed
      if (!evt.currentTarget.classList.contains(style)) {
        evt.currentTarget.classList.add(style)
      }
    }
    onMouseOver();
  }

  return <>
    <div
      className={classes}
      style={styles}
      onClick={handleHotspotClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onMouseOver={handleMouseOver}
      onMouseOut={onMouseOut}
      data-ratio={markerRatio}
      data-id={rawHotspot.id}
      data-temp-id={rawHotspot.tempId}
      data-change-key={rawHotspot.changeKey}
      key={resetKey}
    >
      {content({playerWidth: containerDimensions.width, playerHeight: containerDimensions.height, styles: contentStyles, onImageLoaded})}
    </div>
  </>
}
